import { useDbFunctions } from '@/talons';
import { buildImageSrc, buildImageSrcError } from '@/shared';
import { useState, useEffect } from 'react';
import { useTables } from '@/hooks';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants';

const StyledProductImage = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: contain;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0.25;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 0.25;
  }
`;  

const FallbackProductImage = styled.div`
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 4px;
  object-fit: contain;
  background-color: ${colors.gray.gray50};
  animation: ${fadeIn} 1.5s ease-in-out forwards infinite;
`;

export type Props = {
  COD_PRODUTO: number;
};

const ProductImage = ({ COD_PRODUTO }: Props) => {
  const { tables } = useTables();
  const { getImageFromDB } = useDbFunctions();

  const [image, setImage] = useState<string | undefined>();

  useEffect(() => {
    const loadImage = async () => {
      const img = tables.current.imagens?.[COD_PRODUTO]
      if (img) {
        setImage(img);
        return;
      }
      
      const image = await getImageFromDB(COD_PRODUTO)
      
      if (!!image) {
        const imageSrc = buildImageSrc(image);
        setImage(imageSrc);
        tables.current.imagens[COD_PRODUTO] = imageSrc;
        return;
      };
      
      const fallbackImage = buildImageSrc();
      tables.current.imagens[COD_PRODUTO] = fallbackImage;
      setImage(fallbackImage);
    }

    if (image || !COD_PRODUTO) return;
    
    loadImage();
  }, [image, COD_PRODUTO]);
  
  if (!image) return <FallbackProductImage />;

  return (
    <StyledProductImage
      alt={`imagem do produto ${COD_PRODUTO}`}
      id={`product-image-${COD_PRODUTO}`}
      src={image}
      onError={buildImageSrcError}
      loading="lazy"
      decoding="async"
    />
  );
};

export default ProductImage;
